import { Box, Flex, rem, SimpleGrid, Text, useMantineColorScheme } from "@mantine/core";

interface FollowUpSuggestionsProps {
    suggestions: string[];
    onSuggestionClick: (suggestion: string) => void;
    /** Maximum number of suggestions to display. Defaults to all suggestions. */
    maxSuggestions?: number;
    /** Use compact styling (less padding, single column). Defaults to false. */
    compact?: boolean;
}

export const FollowUpSuggestions = ({
    suggestions,
    onSuggestionClick,
    maxSuggestions,
    compact = false,
}: FollowUpSuggestionsProps) => {
    const { colorScheme } = useMantineColorScheme();

    if (!suggestions || suggestions.length === 0) {
        return null;
    }

    // Limit suggestions if maxSuggestions is specified
    const displayedSuggestions = maxSuggestions
        ? suggestions.slice(0, maxSuggestions)
        : suggestions;

    return (
        <Box
            style={(theme) => ({
                marginTop: compact ? rem(16) : rem(32),
                paddingTop: compact ? rem(12) : rem(24),
                marginBottom: rem(0),
                [`@media (maxWidth: ${theme.breakpoints.xs})`]: {
                    marginBottom: compact ? rem(0) : rem(60),
                },
                position: "relative",
                "&::before": compact
                    ? undefined
                    : {
                          content: '""',
                          position: "absolute",
                          top: 0,
                          left: "50%",
                          transform: "translateX(-50%)",
                          width: "60px",
                          height: "2px",
                          background: `linear-gradient(90deg, ${
                              colorScheme === "dark" ? theme.colors.gray[6] : theme.colors.gray[4]
                          }, ${colorScheme === "dark" ? theme.colors.blue[6] : theme.colors.blue[5]}, ${
                              colorScheme === "dark" ? theme.colors.gray[6] : theme.colors.gray[4]
                          })`,
                          borderRadius: "1px",
                      },
            })}
        >
            {!compact && (
                <Flex direction="column" align="center" mb={rem(20)}>
                    <Text
                        size="sm"
                        fw={500}
                        c="dimmed"
                        style={{
                            textTransform: "uppercase",
                            letterSpacing: "0.5px",
                            fontSize: rem(11),
                            marginBottom: rem(4),
                        }}
                    >
                        Suggested follow-ups
                    </Text>
                    <Text size="xs" c="dimmed" style={{ opacity: 0.7 }}>
                        Click any question to continue the conversation
                    </Text>
                </Flex>
            )}

            <SimpleGrid
                cols={compact ? 1 : { base: 1, sm: 2 }}
                spacing={compact ? rem(8) : { base: rem(10), sm: rem(12) }}
            >
                {displayedSuggestions.map((suggestion, index) => (
                    <Box
                        key={`suggestion-${suggestion.slice(0, 20)}-${index}`}
                        style={(theme) => ({
                            border: `1px solid ${
                                colorScheme === "dark" ? theme.colors.dark[4] : theme.colors.gray[3]
                            }`,
                            borderRadius: compact ? rem(6) : rem(8),
                            padding: compact ? rem(10) : rem(16),
                            cursor: "pointer",
                            transition: "all 0.15s cubic-bezier(0.4, 0, 0.2, 1)",
                            backgroundColor:
                                colorScheme === "dark" ? theme.colors.dark[6] : theme.white,
                            position: "relative",
                            "&::before": {
                                content: '""',
                                position: "absolute",
                                top: 0,
                                left: 0,
                                right: 0,
                                height: "2px",
                                background: `linear-gradient(90deg, ${theme.colors.blue[5]}, ${theme.colors.purple[5]})`,
                                borderRadius: `${rem(8)} ${rem(8)} 0 0`,
                                opacity: 0,
                                transition: "opacity 0.15s ease",
                            },
                            "&:hover": {
                                transform: "translateY(-1px)",
                                boxShadow: `0 4px 12px ${
                                    colorScheme === "dark"
                                        ? "rgba(0, 0, 0, 0.3)"
                                        : "rgba(0, 0, 0, 0.08)"
                                }`,
                                borderColor:
                                    colorScheme === "dark"
                                        ? theme.colors.gray[5]
                                        : theme.colors.gray[4],
                                "&::before": {
                                    opacity: 1,
                                },
                            },
                            "&:active": {
                                transform: "translateY(0px)",
                                transition: "transform 0.05s ease",
                            },
                        })}
                        onClick={() => onSuggestionClick(suggestion)}
                    >
                        <Text
                            size={compact ? "xs" : "sm"}
                            style={(theme) => ({
                                lineHeight: 1.4,
                                color:
                                    colorScheme === "dark"
                                        ? theme.colors.gray[2]
                                        : theme.colors.gray[7],
                                fontWeight: 400,
                            })}
                        >
                            {suggestion}
                        </Text>
                    </Box>
                ))}
            </SimpleGrid>
        </Box>
    );
};
